import React from 'react';
import { Field } from '@globalvisioninc/ui/field';
import styles from './GVTextField.module.css';

export interface GVTextFieldProps {
  label?: React.ReactNode;
  value?: string | null;
  defaultValue?: string | null;
  /**
   * Called when the field value changes. Receives the native change event so
   * form libraries (e.g. Formik `field.onChange`) can read `event.target.name`
   * and `event.target.value`. Prefer `onValueChange` when you only need the
   * next value.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /**
   * Called when the field value changes with the next value. Prefer this over
   * `onChange` when you do not need the native event.
   */
  onValueChange?: (value: string | null) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClick?: React.MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  name?: string;
  id?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  hasError?: boolean;
  message?: string | null;
  isLabelHidden?: boolean;
  isLabelDynamic?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  className?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  type?: React.HTMLInputTypeAttribute;
  required?: boolean;
  tabIndex?: number;
  maxLength?: number;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  /** Optional start element (e.g. a search icon). */
  elementStart?: React.ReactNode;
  /** Optional end element (e.g. a search icon). */
  elementEnd?: React.ReactNode;
  /** Field actions composed after the input (e.g. icon buttons). */
  children?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  'aria-label'?: string;
  'data-testid'?: string;
  isAutoSized?: boolean;
}

const GVTextField = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, GVTextFieldProps>(
  (
    {
      elementStart,
      elementEnd,
      children,
      maxLength,
      min,
      max,
      step,
      label,
      value,
      defaultValue,
      onChange,
      onValueChange,
      onBlur,
      onFocus,
      onClick,
      onKeyDown,
      onKeyUp,
      name,
      id,
      placeholder,
      isDisabled,
      isReadOnly,
      hasError,
      message,
      multiline,
      rows,
      className,
      autoFocus,
      autoComplete,
      type,
      isLabelHidden = false,
      isLabelDynamic,
      textAlign,
      required,
      tabIndex,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      isAutoSized,
    },
    ref,
  ) => {
    const sharedInputProps = {
      name,
      autoFocus,
      autoComplete,
      onClick,
      onKeyDown,
      onKeyUp,
      required,
      tabIndex,
      maxLength,
      min,
      max,
      step,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
    };

    return (
      <Field.Root
        ref={ref}
        id={id}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onValueChange={onValueChange}
        onBlur={onBlur}
        onFocus={onFocus}
        hasError={hasError}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isLabelHidden={isLabelHidden}
        isLabelDynamic={isLabelDynamic}
        textAlign={textAlign}
        elementStart={elementStart}
        elementEnd={elementEnd}
        message={message}
        placeholder={placeholder}
        type={type}
        className={className}
        isAutoSized={isAutoSized}
      >
        {label != null && label !== '' ? (
          <Field.Label>
            {label}
            {required ? (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            ) : null}
          </Field.Label>
        ) : null}
        {multiline ? <Field.Textarea rows={rows} {...sharedInputProps} /> : <Field.Input {...sharedInputProps} />}
        {children}
      </Field.Root>
    );
  },
);

GVTextField.displayName = 'GVTextField';

export default GVTextField;
